/** venture/KitPortal.tsx — 14단계 「자료수집 링크」: 아직 못 채운 항목만 담은 자료수집 포털을 만들고 링크를 준다 (2026-09-24)
 *  · 항목 제목·설명글·링크·민감 여부는 kit13defs 의 정의(지도사가 쓰던 포털 항목) 그대로
 *  · 포털이 이미 있으면 「미입력 항목으로 다시 맞춤」(제출물 있는 항목은 백엔드가 남겨 둔다)
 *  · 제출물은 useKitResults 가 읽어 와 키트 행에 「자료수집 제출」로 나타난다 */
import React, { useState } from 'react';
import { mutStore } from './stepcommon';
import { createCollectPortal, syncCollectPortal } from './api';
import type { CollectItemPayload } from './api';
import { regCoOf } from './coterms';
import { kitMissing, portalId, portalKey } from './kit13';
import { copyText } from './VicsStage';
import { CERT_SHORT, officeOf, portalOrigin } from './types';
import type { Project, CoCard, CertMode } from './types';

interface Props { p: Project; co: CoCard; mode: CertMode; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }

const portalLink = (pid: string): string => portalOrigin() + '/api/portal/' + pid + '/share';

export default function KitPortal({ p, co, mode, onPatch, toast }: Props) {
  const [busy, setBusy] = useState(false);
  const missing = kitMissing(p, co, mode);
  const cpid = portalId(p, co.id, mode);
  const rc = regCoOf(p, co);
  const section = '투자확인서 발급 · ' + CERT_SHORT[mode];
  const items = (): CollectItemPayload[] => missing.map((it) => ({
    clientId: 'k13_' + mode + '_' + it.slot,
    label: it.label,
    type: it.kind === 'file' ? 'file' : it.kind === 'memo' ? 'text_long' : 'text',
    required: true,
    sensitive: !!it.sensitive,
    section,
    description: it.desc || it.hint || '',
    guides: it.link ? [{ type: 'link', label: '사이트 바로가기', url: it.link }] : [],
  }));
  const create = async () => {
    if (busy) return;
    if (!missing.length) { toast('요청할 항목이 없습니다 — 모두 채워졌습니다'); return; }
    setBusy(true);
    try {
      const res = await createCollectPortal(p.id, {
        name: co.name || '(법인)', biz_no: co.bizno || '', ceo: (rc && rc.ceo) || '', ceo_phone: (rc && rc.mgrTel) || '', ceo_email: '',
        label: '투자확인서 발급 (' + CERT_SHORT[mode] + ')', items: items(), brand: officeOf(p), companyId: co.companyId || null,
      });
      onPatch(mutStore(p, 's13', (s) => { s.txt = s.txt || {}; s.txt[portalKey(co.id, mode)] = res.project_id; }));
      const url = portalLink(res.project_id);
      await copyText(url, toast, '자료수집 링크를 만들고 복사했습니다 · 미입력 ' + missing.length + '건');
    } catch (e) { toast('링크 생성 실패: ' + (e as Error).message); } finally { setBusy(false); }
  };
  const resync = async () => {
    if (busy || !cpid) return;
    setBusy(true);
    try {
      const r = await syncCollectPortal(p.id, cpid, items(), { name: co.name || '', ceo: (rc && rc.ceo) || '', brand: officeOf(p), companyId: co.companyId || null });
      toast('포털 항목을 미입력 ' + missing.length + '건으로 맞췄습니다' + (r.kept ? ' · 제출물 있는 ' + r.kept + '건은 유지' : ''));
    } catch (e) { toast('포털 반영 실패: ' + (e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="kitportal">
      <span className="rf">📨 자료수집 링크 <span className="s3muted">{missing.length ? '미입력 ' + missing.length + '건만 담아 만듭니다' : '요청할 항목 없음'}</span></span>
      <span className="grow" />
      {cpid ? (<>
        <button type="button" className="rbtn" onClick={() => copyText(portalLink(cpid), toast, '자료수집 링크를 복사했습니다')}>🔗 링크 복사</button>
        <a className="rbtn" href={portalLink(cpid)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>포털 열기</a>
        <button type="button" className="rbtn" disabled={busy || !missing.length} title="지금 미입력인 항목으로 포털 항목을 다시 맞춥니다 (제출물 있는 항목은 유지)" onClick={resync}>{busy ? '반영 중…' : '미입력 항목으로 다시 맞춤'}</button>
      </>) : (
        <button type="button" className="rbtn ok" disabled={busy || !missing.length} onClick={create}>{busy ? '만드는 중…' : '링크 생성 (미입력 ' + missing.length + '건)'}</button>
      )}
    </div>
  );
}
